// What? A reusable file uploader component with drag-and-drop support
// Why? To provide consistent file upload functionality across the application
// How? Uses react-dropzone for drag-and-drop, handles file uploads, tracks progress, and manages file references

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import {
  Box,
  Button,
  CircularProgress,
  DialogTitle,
  IconButton,
  Modal,
  ModalDialog,
  Stack,
  Typography,
} from "@mui/joy";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DescriptionIcon from "@mui/icons-material/Description";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";

import { useWorkspaceContext } from "../../../../providers/WorkspaceProvider";
import { useAuthFetch } from "../../../../utils/useAuthFetch";
import {
  FileUploaderProps,
  FileUploaderRef,
  FileWithPreview,
  ExistingFile,
  UploadFileReference,
  PersistedUploadJob,
} from "../../types/documentTypes";

// --- localStorage helpers for persisting upload job IDs ---

const MAX_UPLOAD_JOB_TRACKING_MS = 60 * 60 * 1000; // 60 minutes
const STALE_THRESHOLD_MS = MAX_UPLOAD_JOB_TRACKING_MS;
const POLL_INTERVAL_MS = 5000;
const STATUS_POLL_TIMEOUT_MS = 60000;
const UPLOAD_STALL_TIMEOUT_MS = 30000;
const UPLOAD_RESPONSE_TIMEOUT_MS = 60000;

interface UploadJobStatusResponse {
  status: string;
  source_document_id?: number;
  index_status?: "indexing" | "indexed" | "failed" | null;
  fileReference?: UploadFileReference;
  error_message?: string;
}

function createClientUploadAttemptId(): string {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

// After this many CONSECUTIVE 404 ("Job not found") responses on a job-status
// poll, treat the job as gone (orphaned by a server restart or expired) and
// STOP re-polling — rather than retrying for the full tracking window and
// flooding the user with "Failed to check restored job status" toasts (#598).
// A few transient 404s during a brief restart window are absorbed first; any
// non-404 error or a successful poll resets the streak.
export const MAX_CONSECUTIVE_NOT_FOUND = 3;

// useAuthFetch attaches the HTTP status to thrown errors as `.status`.
export function isJobNotFoundError(err: unknown): boolean {
  return (err as { status?: number } | null | undefined)?.status === 404;
}

export function shouldStopPollingAfterNotFound(
  consecutiveNotFound: number,
): boolean {
  return consecutiveNotFound >= MAX_CONSECUTIVE_NOT_FOUND;
}

export function isAwaitingUploadStatus(status: string): boolean {
  return status === "awaiting_upload" || status === "accepting_upload";
}

export function getStorageKey(workspaceId: string): string {
  return `mm_upload_jobs_${workspaceId}`;
}

export function loadPersistedJobs(workspaceId: string): PersistedUploadJob[] {
  try {
    const raw = localStorage.getItem(getStorageKey(workspaceId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function savePersistedJobs(
  workspaceId: string,
  jobs: PersistedUploadJob[],
): void {
  try {
    if (jobs.length === 0) {
      localStorage.removeItem(getStorageKey(workspaceId));
    } else {
      localStorage.setItem(getStorageKey(workspaceId), JSON.stringify(jobs));
    }
  } catch {
    // Quota exceeded or other storage error -- degrade silently
  }
}

function addPersistedJob(workspaceId: string, job: PersistedUploadJob): void {
  const jobs = loadPersistedJobs(workspaceId);
  const existingIndex = jobs.findIndex((j) => j.jobId === job.jobId);
  if (existingIndex === -1) {
    savePersistedJobs(workspaceId, [...jobs, job]);
    return;
  }
  savePersistedJobs(
    workspaceId,
    jobs.map((existing, index) =>
      index === existingIndex ? { ...existing, ...job } : existing,
    ),
  );
}

export function removePersistedJob(workspaceId: string, jobId: string): void {
  const jobs = loadPersistedJobs(workspaceId);
  savePersistedJobs(
    workspaceId,
    jobs.filter((j) => j.jobId !== jobId),
  );
}

function cleanStaleJobs(workspaceId: string): PersistedUploadJob[] {
  const jobs = loadPersistedJobs(workspaceId);
  const now = Date.now();
  const fresh = jobs.filter((j) => now - j.startedAt < STALE_THRESHOLD_MS);
  savePersistedJobs(workspaceId, fresh);
  return fresh;
}

// --- end localStorage helpers ---

const getFileIcon = (fileName: string | undefined) => {
  if (!fileName)
    return <DescriptionIcon sx={{ fontSize: "2rem", color: "neutral.500" }} />;

  const ext = fileName.split(".").pop()?.toLowerCase();
  switch (ext) {
    case "pdf":
      return (
        <PictureAsPdfIcon sx={{ fontSize: "2rem", color: "danger.500" }} />
      );
    case "doc":
    case "docx":
    default:
      return (
        <DescriptionIcon sx={{ fontSize: "2rem", color: "neutral.500" }} />
      );
  }
};

const FileUploader = React.forwardRef<FileUploaderRef, FileUploaderProps>(
  (props, ref) => {
    const {
      maxFiles = Infinity,
      maxFilesPerUpload = Infinity,
      existingFiles = [],
      disabled = false,
      maxSize,
      accept,
      documentType,
      uploadEndpoint,
      onUploadComplete,
      onUploadError,
      onUploadActivityChange,
      onDeleteFile,
      requireFileReference,
      label = "Drag and drop files here, or click to select files",
      className,
      requireConfirmation = false,
      confirmationMessage = "This action will delete all existing sections. Are you sure you want to continue?",
      confirmationTitle = "Confirm Upload",
      confirmationButtons = [
        { label: "Cancel", variant: "secondary" },
        { label: "Continue", variant: "primary" },
      ],
      additionalFields = {},
      metadata,
      onFileReferenceReceived,
      onReindexFile,
    } = props;

    const [pendingFiles, setPendingFiles] = useState<FileWithPreview[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [pendingUpload, setPendingUpload] = useState<File[]>([]);
    const [deletingFileId, setDeletingFileId] = useState<number | null>(null);
    const [activeUploadCount, setActiveUploadCount] = useState(0);
    const activeUploadOperationsRef = useRef<Set<string>>(new Set());
    const uploadActivityCallbackRef = useRef(onUploadActivityChange);

    const authFetch = useAuthFetch();
    const { workspace } = useWorkspaceContext();
    const workspaceId = workspace?.id;

    useEffect(() => {
      uploadActivityCallbackRef.current = onUploadActivityChange;
    }, [onUploadActivityChange]);

    // FE #1461: an interrupted transfer (network drop mid-upload, or a job
    // restored on mount that never finished) clears its own operation's
    // active flag in the same code path that sets uploadStatus:
    // "interrupted" -- the source was never indexed, but activeUploadCount
    // alone would already read 0. Generate gating (ConfigPanel.tsx's
    // hasActiveSourceUploads) has no other signal for this state: it isn't
    // a legal indexStatus value, so hasIndexingIssue cannot see it either.
    // Report activity as long as any card is still showing "interrupted",
    // so Generate stays blocked until the user retries or removes the file.
    const hasInterruptedUploads = pendingFiles.some(
      (file) => file.uploadStatus === "interrupted",
    );

    useEffect(() => {
      uploadActivityCallbackRef.current?.(
        activeUploadCount > 0 || hasInterruptedUploads,
      );
    }, [activeUploadCount, hasInterruptedUploads]);

    useEffect(
      () => () => {
        uploadActivityCallbackRef.current?.(false);
      },
      [],
    );

    const setUploadOperationActive = useCallback(
      (operationId: string, isActive: boolean) => {
        const activeOperations = activeUploadOperationsRef.current;
        if (isActive) {
          activeOperations.add(operationId);
        } else {
          activeOperations.delete(operationId);
        }
        setActiveUploadCount(activeOperations.size);
      },
      [],
    );

    // Once a processing job has a persisted SourceDocument row, the
    // server-backed existing file card is the single visual representation.
    // Keep polling the job, but remove its temporary card.
    useEffect(() => {
      const existingIds = new Set(existingFiles.map((file) => file.id));
      if (existingIds.size === 0) return;
      setPendingFiles((prev) => {
        const reconciled = prev.filter(
          (file) =>
            !file.sourceDocumentId || !existingIds.has(file.sourceDocumentId),
        );
        return reconciled.length === prev.length ? prev : reconciled;
      });
    }, [existingFiles]);

    // Poll for job completion
    const pollJobStatus = useCallback(
      async (
        jobId: string,
        fileName: string,
        onProgress?: (status: UploadJobStatusResponse) => void,
      ): Promise<{
        status: string;
        fileReference?: UploadFileReference;
        error_message?: string;
        sourceDocumentId?: number;
      }> => {
        const statusUrl = `/api/workspace/meridian/documents/jobs/${jobId}/status`;
        const pollingDeadline = Date.now() + MAX_UPLOAD_JOB_TRACKING_MS;
        let attempt = 0;
        let consecutiveNotFound = 0;

        while (Date.now() < pollingDeadline) {
          attempt += 1;
          const remainingMs = pollingDeadline - Date.now();
          const requestTimeout = Math.min(STATUS_POLL_TIMEOUT_MS, remainingMs);

          try {
            const statusResponse = await authFetch(
              `${statusUrl}?workspace_id=${workspaceId}`,
              "fetch job status",
              undefined,
              requestTimeout,
            );
            onProgress?.(statusResponse as UploadJobStatusResponse);

            if (statusResponse.status === "completed") {
              return {
                status: "completed",
                fileReference: statusResponse.fileReference,
                sourceDocumentId: statusResponse.source_document_id,
              };
            }

            if (statusResponse.status === "failed") {
              return {
                status: "failed",
                error_message:
                  statusResponse.error_message || "Processing failed",
                sourceDocumentId: statusResponse.source_document_id,
              };
            }

            // Successful poll (still pending/processing) -- the job exists, so
            // reset the consecutive not-found streak.
            consecutiveNotFound = 0;
          } catch (err) {
            if (isJobNotFoundError(err)) {
              // The job record is gone (e.g. the server was restarted and the
              // in-memory job store lost it, or the job expired). Absorb a few
              // transient 404s during a brief restart window, but after the
              // threshold STOP re-polling rather than flooding for the full
              // tracking window with "Job not found" (#598). The uploaded file
              // itself is durable server-side; only in-flight status tracking
              // is lost.
              consecutiveNotFound += 1;
              if (shouldStopPollingAfterNotFound(consecutiveNotFound)) {
                console.warn(
                  `[FileUploader] job ${jobId} (${fileName}) returned 404 ` +
                    `${consecutiveNotFound}x consecutively -- giving up; the ` +
                    `job no longer exists (server may have restarted or the ` +
                    `job expired).`,
                );
                return {
                  status: "not_found",
                  error_message:
                    "Upload tracking was lost (the server may have " +
                    "restarted). The file may have uploaded successfully -- " +
                    "please refresh and check your documents.",
                };
              }
            } else {
              // Transient error (timeout / HTTP 500 / network) during heavy OCR
              // processing -- safe to retry. Reset the not-found streak so
              // transient blips don't count toward the give-up bound.
              consecutiveNotFound = 0;
              console.warn(
                `Status poll attempt ${attempt} failed for job ${jobId}, retrying...`,
                err instanceof Error ? err.message : err,
              );
            }
          }

          const remainingAfterPollMs = pollingDeadline - Date.now();
          if (remainingAfterPollMs <= 0) {
            break;
          }

          // Still processing (or timeout), wait before next poll
          await new Promise((resolve) =>
            setTimeout(
              resolve,
              Math.min(POLL_INTERVAL_MS, remainingAfterPollMs),
            ),
          );
        }

        // No terminal job verdict was observed: retry must reconcile this job,
        // not allocate a second upload that could duplicate a live parse.
        return { status: "timed_out", error_message: "Processing timeout" };
      },
      [authFetch, workspaceId],
    );

    const uploadFile = useCallback(
      async (file: FileWithPreview) => {
        if (disabled) {
          return;
        }

        const operationId = createClientUploadAttemptId();
        setUploadOperationActive(operationId, true);
        const clientUploadAttemptId =
          file.clientUploadAttemptId || createClientUploadAttemptId();
        const supportsPreRegistration =
          uploadEndpoint.endsWith("/upload/source-document") &&
          metadata?.documentId !== undefined &&
          metadata?.sourceFieldId !== undefined;

        let activeJobId: string | undefined = file.jobId;
        let terminalJobFailed = false;
        try {
          if (!workspaceId) {
            throw new Error(
              "Unable to upload because no workspace is selected.",
            );
          }

          const needsRegistration =
            supportsPreRegistration &&
            (!activeJobId || file.registrationConfirmed !== true);

          if (needsRegistration) {
            setPendingFiles((prev) =>
              prev.map((pendingFile) =>
                pendingFile.name === file.name
                  ? {
                      ...pendingFile,
                      uploadStatus: "registering",
                      clientUploadAttemptId,
                    }
                  : pendingFile,
              ),
            );

            // The client-generated UUID is also the server job ID. Persist it
            // before registration so a refresh cannot lose the upload card
            // while the lightweight registration request is in flight.
            activeJobId = activeJobId || clientUploadAttemptId;
            addPersistedJob(workspaceId, {
              jobId: activeJobId,
              clientUploadAttemptId,
              registrationConfirmed: false,
              fileName: file.name,
              fileSize: file.size,
              startedAt: Date.now(),
              uploadEndpoint,
              sourceFieldId: metadata?.sourceFieldId as number,
              documentId: metadata?.documentId as number,
            });
            const registration = await authFetch(
              `${uploadEndpoint}/register?workspace_id=${workspaceId}`,
              "register source upload",
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  client_upload_id: clientUploadAttemptId,
                  filename: file.name,
                  document_type: documentType,
                  file_size: file.size,
                  mime_type:
                    file.originalFile.type || "application/octet-stream",
                  source_field_id: metadata?.sourceFieldId,
                  document_id: metadata?.documentId,
                }),
              },
            );
            if (!registration.job_id) {
              throw new Error("Upload registration returned no job ID.");
            }
            if (registration.job_id !== activeJobId) {
              throw new Error(
                "Upload registration returned a mismatched job ID.",
              );
            }
            addPersistedJob(workspaceId, {
              jobId: activeJobId,
              clientUploadAttemptId,
              registrationConfirmed: true,
              fileName: file.name,
              fileSize: file.size,
              startedAt: Date.now(),
              uploadEndpoint,
              sourceFieldId: metadata?.sourceFieldId as number,
              documentId: metadata?.documentId as number,
            });
          }

          // "Uploading..." now specifically means the durable registration is
          // confirmed and the multipart byte transfer is about to begin.
          setPendingFiles((prev) =>
            prev.map((f) =>
              f.name === file.name
                ? {
                    ...f,
                    uploadStatus: "uploading",
                    clientUploadAttemptId,
                    registrationConfirmed: true,
                    jobId: activeJobId,
                  }
                : f,
            ),
          );

          const formData = new FormData();
          formData.append("file", file.originalFile);
          formData.append("documentType", documentType);
          if (activeJobId) {
            formData.append("upload_job_id", activeJobId);
          }

          // Add any additional fields
          Object.entries(additionalFields || {}).forEach(([key, value]) => {
            formData.append(key, value);
          });

          if (metadata) {
            Object.entries(metadata).forEach(([key, value]) => {
              formData.append(`metadata.${key}`, value.toString());
            });
          }

          // Upload file (returns immediately with job_id for async processing)
          const response = await authFetch(
            `${uploadEndpoint}?workspace_id=${workspaceId}`,
            "upload file",
            {
              method: "POST",
              body: formData,
            },
            undefined,
            {
              multipartUpload: {
                stallMs: UPLOAD_STALL_TIMEOUT_MS,
                responseMs: UPLOAD_RESPONSE_TIMEOUT_MS,
              },
            },
          );

          // Check if this is an async response with job_id
          if (response.job_id) {
            activeJobId = response.job_id;
            // Update status to "processing" and store job ID
            setPendingFiles((prev) =>
              prev.map((f) =>
                f.name === file.name
                  ? { ...f, uploadStatus: "processing", jobId: response.job_id }
                  : f,
              ),
            );

            // Persist to localStorage so we can resume polling after remount
            if (workspaceId) {
              addPersistedJob(workspaceId, {
                jobId: response.job_id,
                clientUploadAttemptId,
                registrationConfirmed: true,
                fileName: file.name,
                fileSize: file.size,
                startedAt: Date.now(),
                uploadEndpoint,
                sourceFieldId: metadata?.sourceFieldId as number | undefined,
                documentId: metadata?.documentId as number | undefined,
              });
            }

            // Poll for completion
            const jobResult = await pollJobStatus(
              response.job_id,
              file.name,
              (statusResponse) => {
                const sourceDocumentId =
                  statusResponse.source_document_id ?? undefined;
                if (!sourceDocumentId) return;
                const hasServerCard = existingFiles.some(
                  (existingFile) => existingFile.id === sourceDocumentId,
                );
                setPendingFiles((prev) =>
                  hasServerCard
                    ? prev.filter(
                        (pendingFile) => pendingFile.jobId !== response.job_id,
                      )
                    : prev.map((pendingFile) =>
                        pendingFile.jobId === response.job_id
                          ? {
                              ...pendingFile,
                              sourceDocumentId,
                              uploadStatus:
                                statusResponse.index_status === "indexing"
                                  ? "indexing"
                                  : pendingFile.uploadStatus,
                            }
                          : pendingFile,
                      ),
                );
              },
            );

            if (jobResult.status === "completed" && jobResult.fileReference) {
              if (workspaceId) removePersistedJob(workspaceId, response.job_id);

              if (onFileReferenceReceived) {
                onFileReferenceReceived(jobResult.fileReference);
              }

              onUploadComplete?.({
                message: "File uploaded successfully",
                fileReference: jobResult.fileReference,
              });
              setPendingFiles((prev) =>
                prev.filter((f) => f.name !== file.name),
              );
            } else {
              if (workspaceId) removePersistedJob(workspaceId, response.job_id);
              // Only a pre-persistence failure can safely start a new upload.
              // A failed job with a SourceDocument may already have stored the
              // row, so a fresh attempt could create a duplicate source.
              terminalJobFailed =
                jobResult.status === "failed" && !jobResult.sourceDocumentId;
              throw new Error(jobResult.error_message || "Processing failed");
            }
          } else {
            // Fallback: synchronous response (backward compatibility)
            const result = response as {
              fileReference?: UploadFileReference;
              message?: string;
            };

            const fileRef = result.fileReference;

            if (requireFileReference && !fileRef) {
              throw new Error(
                "Upload succeeded but no file reference was returned.",
              );
            }

            if (onFileReferenceReceived && fileRef) {
              onFileReferenceReceived(fileRef);
            }

            onUploadComplete?.(result);
            setPendingFiles((prev) => prev.filter((f) => f.name !== file.name));
          }
        } catch (err) {
          const persistedActiveJob =
            workspaceId && activeJobId
              ? loadPersistedJobs(workspaceId).find(
                  (job) => job.jobId === activeJobId,
                )
              : undefined;
          console.error("FileUploader: Error in uploadFile:", err);
          const error = err instanceof Error ? err : new Error("Upload failed");
          const registeredJobStillTracked = Boolean(persistedActiveJob);
          // A failed accepted job cannot be claimed again. A same-session
          // refresh must register a new attempt with the retained real File.
          const nextAttemptId = terminalJobFailed
            ? createClientUploadAttemptId()
            : clientUploadAttemptId;
          setPendingFiles((prev) =>
            prev.map((f) =>
              f.name === file.name
                ? {
                    ...f,
                    uploadStatus: registeredJobStillTracked
                      ? "interrupted"
                      : "error",
                    uploadError:
                      error.name === "TimeoutError"
                        ? error.message
                        : registeredJobStillTracked
                          ? "Upload interrupted. Retry with this file."
                          : error.message,
                    jobId: terminalJobFailed ? undefined : activeJobId,
                    clientUploadAttemptId: nextAttemptId,
                    registrationConfirmed: terminalJobFailed
                      ? false
                      : persistedActiveJob?.registrationConfirmed,
                  }
                : f,
            ),
          );
          if (!registeredJobStillTracked) {
            onUploadError?.(error.message);
          }
        } finally {
          setUploadOperationActive(operationId, false);
        }
      },
      [
        additionalFields,
        authFetch,
        disabled,
        documentType,
        existingFiles,
        metadata,
        onFileReferenceReceived,
        onUploadComplete,
        onUploadError,
        pollJobStatus,
        requireFileReference,
        setUploadOperationActive,
        uploadEndpoint,
        workspaceId,
      ],
    );

    // Restore persisted upload jobs on mount
    const restoredRef = useRef(false);
    useEffect(() => {
      if (restoredRef.current || !workspaceId) return;
      restoredRef.current = true;

      const freshJobs = cleanStaleJobs(workspaceId);
      // Filter to jobs matching this FileUploader instance
      const myJobs = freshJobs.filter((job) => {
        if (job.uploadEndpoint !== uploadEndpoint) return false;
        // When metadata has documentId, only restore jobs for this specific document
        const myDocumentId = metadata?.documentId as number | undefined;
        if (myDocumentId !== undefined && job.documentId !== myDocumentId) {
          return false;
        }
        // When metadata has sourceFieldId, only restore jobs for this specific source field
        const mySourceFieldId = metadata?.sourceFieldId as number | undefined;
        if (mySourceFieldId !== undefined) {
          return job.sourceFieldId === mySourceFieldId;
        }
        return true;
      });

      if (myJobs.length === 0) return;

      // For each persisted job, check status first before showing a pending entry.
      // If the job already completed while we were away, just clean up localStorage
      // without adding a visual "processing" entry (the server data already covers it).
      for (const job of myJobs) {
        const restoreOperationId = `restore:${job.jobId}`;
        setUploadOperationActive(restoreOperationId, true);
        const reconcileSourceDocument = (
          statusResponse: UploadJobStatusResponse,
        ): number | undefined => {
          const sourceDocumentId =
            statusResponse.source_document_id ?? undefined;
          if (!sourceDocumentId) return undefined;
          const hasServerCard = existingFiles.some(
            (existingFile) => existingFile.id === sourceDocumentId,
          );
          setPendingFiles((prev) =>
            hasServerCard
              ? prev.filter((file) => file.jobId !== job.jobId)
              : prev.map((file) =>
                  file.jobId === job.jobId
                    ? {
                        ...file,
                        sourceDocumentId,
                        uploadStatus:
                          statusResponse.index_status === "indexing"
                            ? "indexing"
                            : file.uploadStatus,
                      }
                    : file,
                ),
          );
          return sourceDocumentId;
        };
        const statusUrl = `/api/workspace/meridian/documents/jobs/${job.jobId}/status`;
        authFetch(
          // Tag intentionally contains "fetch" so useAuthFetch suppresses the
          // user-facing error toast on failure -- a 404 here (orphaned restored
          // job after a restart) is expected and handled below, not a user error
          // ("Failed to check restored job status" flood, #598).
          `${statusUrl}?workspace_id=${workspaceId}`,
          "fetch restored job status",
          undefined,
          10000,
        )
          .then((statusResponse) => {
            const sourceDocumentId = reconcileSourceDocument(statusResponse);
            if (
              statusResponse.status === "completed" &&
              statusResponse.fileReference
            ) {
              // Already done -- clean up localStorage, deliver reference (deduped by ConfigPanel)
              removePersistedJob(workspaceId!, job.jobId);
              onFileReferenceReceived?.(statusResponse.fileReference);
              setUploadOperationActive(restoreOperationId, false);
              return;
            }

            if (statusResponse.status === "failed") {
              removePersistedJob(workspaceId!, job.jobId);
              setUploadOperationActive(restoreOperationId, false);
              return;
            }

            if (isAwaitingUploadStatus(statusResponse.status)) {
              const emptyFile = new File([], job.fileName, {
                type: "application/octet-stream",
              });
              const interruptedFile: FileWithPreview = {
                ...emptyFile,
                preview: "",
                uploadStatus: "interrupted" as const,
                uploadError:
                  "Upload interrupted. Select the same file again to retry.",
                originalFile: emptyFile,
                name: job.fileName,
                size: job.fileSize,
                jobId: job.jobId,
                clientUploadAttemptId: job.clientUploadAttemptId || job.jobId,
                registrationConfirmed: true,
                isRestored: true,
              } as FileWithPreview;

              setPendingFiles((prev) => {
                if (prev.some((f) => f.jobId === job.jobId)) return prev;
                return [...prev, interruptedFile];
              });
              setUploadOperationActive(restoreOperationId, false);
              return;
            }

            // Still processing -- show pending entry and continue polling
            const emptyFile = new File([], job.fileName, {
              type: "application/octet-stream",
            });
            const syntheticFile: FileWithPreview = {
              ...emptyFile,
              preview: "",
              uploadStatus:
                statusResponse.index_status === "indexing"
                  ? ("indexing" as const)
                  : ("processing" as const),
              originalFile: emptyFile,
              name: job.fileName,
              size: job.fileSize,
              jobId: job.jobId,
              sourceDocumentId,
              isRestored: true,
            } as FileWithPreview;

            setPendingFiles((prev) => {
              if (
                sourceDocumentId &&
                existingFiles.some((file) => file.id === sourceDocumentId)
              ) {
                return prev;
              }
              if (prev.some((f) => f.jobId === job.jobId)) return prev;
              return [...prev, syntheticFile];
            });

            void pollJobStatus(job.jobId, job.fileName, reconcileSourceDocument)
              .then((result) => {
                removePersistedJob(workspaceId!, job.jobId);
                if (result.status === "completed" && result.fileReference) {
                  onFileReferenceReceived?.(result.fileReference);
                  onUploadComplete?.({
                    message: "File uploaded successfully",
                    fileReference: result.fileReference,
                  });
                  setPendingFiles((prev) =>
                    prev.filter((f) => f.jobId !== job.jobId),
                  );
                } else {
                  const errorMsg = result.error_message || "Processing failed";
                  setPendingFiles((prev) =>
                    prev.map((f) =>
                      f.jobId === job.jobId
                        ? {
                            ...f,
                            uploadStatus: "error" as const,
                            uploadError: errorMsg,
                          }
                        : f,
                    ),
                  );
                  onUploadError?.(errorMsg);
                }
              })
              .finally(() =>
                setUploadOperationActive(restoreOperationId, false),
              );
          })
          .catch((err) => {
            if (isJobNotFoundError(err)) {
              if (job.registrationConfirmed === false) {
                const emptyFile = new File([], job.fileName, {
                  type: "application/octet-stream",
                });
                const interruptedFile: FileWithPreview = {
                  ...emptyFile,
                  preview: "",
                  uploadStatus: "interrupted" as const,
                  uploadError:
                    "Upload interrupted. Select the same file again to retry.",
                  originalFile: emptyFile,
                  name: job.fileName,
                  size: job.fileSize,
                  jobId: job.jobId,
                  clientUploadAttemptId: job.clientUploadAttemptId || job.jobId,
                  registrationConfirmed: false,
                  isRestored: true,
                } as FileWithPreview;
                setPendingFiles((prev) => {
                  if (prev.some((f) => f.jobId === job.jobId)) return prev;
                  return [...prev, interruptedFile];
                });
                setUploadOperationActive(restoreOperationId, false);
                return;
              }
              // Restored job no longer exists on the server (orphaned by a
              // restart or expired). Purge it from localStorage so it isn't
              // re-restored + re-polled on the next mount, and skip the doomed
              // poll loop entirely -- this is what stops the perpetual "Job not
              // found" cascade after a deploy restart (#598). The uploaded file
              // is durable server-side; only in-flight status tracking is lost.
              removePersistedJob(workspaceId!, job.jobId);
              console.warn(
                `[FileUploader] restored job ${job.jobId} (${job.fileName}) ` +
                  `no longer exists (404) -- purged from localStorage; not re-polling.`,
              );
              setUploadOperationActive(restoreOperationId, false);
              return;
            }
            // Transient status-check failure -- show as pending and resume
            // polling as before (pollJobStatus itself now bounds 404s).
            const emptyFile = new File([], job.fileName, {
              type: "application/octet-stream",
            });
            const syntheticFile: FileWithPreview = {
              ...emptyFile,
              preview: "",
              uploadStatus: "processing" as const,
              originalFile: emptyFile,
              name: job.fileName,
              size: job.fileSize,
              jobId: job.jobId,
              isRestored: true,
            } as FileWithPreview;

            setPendingFiles((prev) => {
              if (prev.some((f) => f.jobId === job.jobId)) return prev;
              return [...prev, syntheticFile];
            });

            void pollJobStatus(job.jobId, job.fileName, reconcileSourceDocument)
              .then((result) => {
                removePersistedJob(workspaceId!, job.jobId);
                if (result.status === "completed" && result.fileReference) {
                  onFileReferenceReceived?.(result.fileReference);
                  setPendingFiles((prev) =>
                    prev.filter((f) => f.jobId !== job.jobId),
                  );
                } else {
                  const errorMsg = result.error_message || "Processing failed";
                  setPendingFiles((prev) =>
                    prev.map((f) =>
                      f.jobId === job.jobId
                        ? {
                            ...f,
                            uploadStatus: "error" as const,
                            uploadError: errorMsg,
                          }
                        : f,
                    ),
                  );
                  onUploadError?.(errorMsg);
                }
              })
              .finally(() =>
                setUploadOperationActive(restoreOperationId, false),
              );
          });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [workspaceId]);

    const processFiles = useCallback(
      (files: File[]) => {
        if (disabled) {
          return;
        }

        const reusedJobIds = new Set<string>();
        const newFiles = files.map((file) => {
          const interrupted = pendingFiles.find(
            (pending) =>
              pending.uploadStatus === "interrupted" &&
              pending.name === file.name &&
              pending.size === file.size &&
              pending.jobId &&
              !reusedJobIds.has(pending.jobId),
          );
          if (interrupted?.jobId) {
            reusedJobIds.add(interrupted.jobId);
          }
          const clientUploadAttemptId =
            interrupted?.clientUploadAttemptId || createClientUploadAttemptId();
          return {
            ...file,
            preview: URL.createObjectURL(file),
            uploadStatus: "pending",
            originalFile: file,
            name: file.name,
            size: file.size,
            jobId: interrupted?.jobId,
            clientUploadAttemptId,
            registrationConfirmed: interrupted?.registrationConfirmed,
          } as FileWithPreview;
        });

        setPendingFiles((prev) => [
          ...prev.filter(
            (pending) => !pending.jobId || !reusedJobIds.has(pending.jobId),
          ),
          ...newFiles,
        ]);
        setError(null);

        newFiles.forEach((file) => uploadFile(file));
      },
      [disabled, pendingFiles, uploadFile],
    );

    const onDrop = useCallback(
      (acceptedFiles: File[]) => {
        if (disabled) {
          return;
        }

        // Check number of files being uploaded at once
        if (acceptedFiles.length > maxFilesPerUpload) {
          setError(
            `Maximum ${maxFilesPerUpload} files can be uploaded at once`,
          );
          return;
        }

        // Check total files limit
        if (
          existingFiles.length + pendingFiles.length + acceptedFiles.length >
          maxFiles
        ) {
          setError(`Maximum ${maxFiles} files allowed in total`);
          return;
        }

        if (requireConfirmation) {
          setPendingUpload(acceptedFiles);
          setShowConfirmation(true);
          return;
        }
        processFiles(acceptedFiles);
      },
      [
        existingFiles.length,
        disabled,
        maxFiles,
        maxFilesPerUpload,
        pendingFiles.length,
        processFiles,
        requireConfirmation,
      ],
    );

    const handleConfirmation = useCallback(
      (confirmed: boolean) => {
        setShowConfirmation(false);
        if (confirmed && pendingUpload.length > 0) {
          processFiles(pendingUpload);
        }
        setPendingUpload([]);
      },
      [pendingUpload, processFiles],
    );

    const { getRootProps, getInputProps } = useDropzone({
      onDrop,
      accept,
      disabled,
      maxSize,
      multiple: true,
      maxFiles: maxFiles - (existingFiles.length || 0),
      onError: (error) => {
        setError(error.message);
      },
      onDropRejected: (fileRejections) => {
        const errors = fileRejections.map((rejection) =>
          rejection.errors
            .map((error) => {
              if (error.code === "file-too-large" && maxSize) {
                const maxMB = Math.round(maxSize / (1024 * 1024));
                return `File is too large. Maximum size is ${maxMB} MB.`;
              }
              return error.message;
            })
            .join(", "),
        );
        setError(errors.join("; "));
      },
    });

    // Handle removal of existing files
    const handleDeleteExisting = async (id: number) => {
      if (disabled || !onDeleteFile) {
        return;
      }

      try {
        setDeletingFileId(id);
        await onDeleteFile(id);
      } catch (err) {
        console.error("FileUploader: Error in delete handler:", err);
        setError("Failed to delete file");
      } finally {
        setDeletingFileId(null);
      }
    };

    // Handle removal of pending files
    const removeFile = (fileToRemove: FileWithPreview) => {
      if (workspaceId && fileToRemove.jobId) {
        removePersistedJob(workspaceId, fileToRemove.jobId);
      }
      setPendingFiles((prev) => prev.filter((f) => f !== fileToRemove));
    };

    return (
      <>
        <Box className={className}>
          {/* Dropzone area */}
          <Box
            {...getRootProps()}
            aria-disabled={disabled}
            sx={{
              border: "2px dashed",
              borderColor: "neutral.300",
              borderRadius: "sm",
              p: 3,
              textAlign: "center",
              cursor: disabled ? "not-allowed" : "pointer",
              opacity: disabled ? 0.6 : 1,
              transition: "all 0.2s ease",
              "&:hover": {
                borderColor: disabled ? "neutral.300" : "primary.500",
                backgroundColor: disabled ? "transparent" : "primary.50",
              },
            }}
          >
            <input {...getInputProps()} />
            <Typography level="body-sm" color="neutral">
              {label}
            </Typography>
          </Box>

          {/* File list */}
          <Stack spacing={1} sx={{ mt: 2 }}>
            {/* Show existing files */}
            {existingFiles.map((file: ExistingFile) => (
              <Box
                key={`existing-${file.id}`}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  p: 2,
                  border: "1px solid",
                  borderColor:
                    file.indexStatus === "failed"
                      ? "danger.400"
                      : file.indexStatus === "pending" ||
                          file.indexStatus === "indexing"
                        ? "warning.400"
                        : "neutral.300",
                  borderRadius: "sm",
                  backgroundColor:
                    file.indexStatus === "failed" ? "danger.50" : "transparent",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    borderColor:
                      file.indexStatus === "failed"
                        ? "danger.500"
                        : "primary.500",
                    boxShadow: "sm",
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  {getFileIcon(file.fileName)}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      level="body-sm"
                      sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {file.fileName}
                    </Typography>
                    <Typography level="body-xs" color="neutral">
                      {(file.fileSize / 1024).toFixed(1)} KB
                    </Typography>
                    {file.indexStatus === "failed" && (
                      <Typography level="body-xs" color="danger">
                        Indexing failed
                        {file.indexError ? `: ${file.indexError}` : ""}
                      </Typography>
                    )}
                    {(file.indexStatus === "pending" ||
                      file.indexStatus === "indexing") && (
                      <Typography level="body-xs" color="warning">
                        {file.indexStatus === "pending"
                          ? "Indexing pending..."
                          : "Indexing in progress..."}
                      </Typography>
                    )}
                  </Box>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  {file.indexStatus === "failed" && onReindexFile && (
                    <IconButton
                      size="sm"
                      variant="outlined"
                      color="warning"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (disabled) {
                          return;
                        }
                        onReindexFile(file.id);
                      }}
                      disabled={disabled}
                      aria-label="Retry indexing"
                    >
                      <RefreshIcon />
                    </IconButton>
                  )}
                  {(file.indexStatus === "pending" ||
                    file.indexStatus === "indexing") && (
                    <CircularProgress size="sm" />
                  )}
                  {deletingFileId === file.id ? (
                    <CircularProgress size="sm" />
                  ) : (
                    <IconButton
                      size="sm"
                      variant="plain"
                      color="neutral"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteExisting(file.id);
                      }}
                      disabled={disabled || deletingFileId !== null}
                      aria-label="Remove file"
                      title={
                        disabled
                          ? "Source documents can only be removed before generation starts or after it finishes"
                          : undefined
                      }
                    >
                      <CloseIcon />
                    </IconButton>
                  )}
                </Box>
              </Box>
            ))}

            {/* Show pending/uploaded files */}
            {pendingFiles.map((file, index) => (
              <Box
                key={`new-${file.name}-${index}`}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  p: 2,
                  border: "1px solid",
                  borderColor:
                    file.uploadStatus === "error"
                      ? "danger.300"
                      : file.uploadStatus === "interrupted"
                        ? "warning.400"
                        : "neutral.300",
                  borderRadius: "sm",
                  transition: "all 0.2s ease",
                  backgroundColor:
                    file.uploadStatus === "success"
                      ? "success.50"
                      : "transparent",
                  "&:hover": {
                    borderColor:
                      file.uploadStatus === "error"
                        ? "danger.500"
                        : file.uploadStatus === "interrupted"
                          ? "warning.500"
                          : "primary.500",
                    boxShadow: "sm",
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  {getFileIcon(file.name)}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      level="body-sm"
                      sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {file.name}
                    </Typography>
                    <Typography level="body-xs" color="neutral">
                      {(file.size / 1024).toFixed(1)} KB
                    </Typography>
                    {(file.uploadStatus === "error" ||
                      file.uploadStatus === "interrupted") &&
                      file.uploadError && (
                        <Typography
                          level="body-xs"
                          color={
                            file.uploadStatus === "interrupted"
                              ? "warning"
                              : "danger"
                          }
                        >
                          {file.uploadError}
                        </Typography>
                      )}
                  </Box>
                </Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  {file.uploadStatus === "registering" && (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <CircularProgress size="sm" />
                      <Typography level="body-xs" color="neutral">
                        Preparing upload...
                      </Typography>
                    </Stack>
                  )}
                  {file.uploadStatus === "uploading" && (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <CircularProgress size="sm" />
                      <Typography level="body-xs" color="neutral">
                        Uploading...
                      </Typography>
                    </Stack>
                  )}
                  {file.uploadStatus === "processing" && (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <CircularProgress size="sm" />
                      <Typography level="body-xs" color="neutral">
                        Processing...
                      </Typography>
                    </Stack>
                  )}
                  {file.uploadStatus === "indexing" && (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <CircularProgress size="sm" />
                      <Typography level="body-xs" color="warning">
                        Indexing...
                      </Typography>
                    </Stack>
                  )}
                  {file.uploadStatus === "interrupted" &&
                    (file.isRestored ? (
                      <Typography level="body-xs" color="warning">
                        Select file to retry
                      </Typography>
                    ) : (
                      <IconButton
                        size="sm"
                        variant="outlined"
                        color="warning"
                        onClick={() => uploadFile(file)}
                        disabled={disabled}
                        aria-label="Retry upload"
                      >
                        <RefreshIcon />
                      </IconButton>
                    ))}
                  {file.uploadStatus === "success" && (
                    <Typography
                      level="body-lg"
                      color="success"
                      sx={{ fontWeight: "bold" }}
                    >
                      Done
                    </Typography>
                  )}
                  {file.uploadStatus === "error" &&
                    (file.isRestored ? (
                      <Typography level="body-xs" color="danger">
                        Re-upload required
                      </Typography>
                    ) : file.sourceDocumentId ? (
                      <Typography level="body-xs" color="danger">
                        Check source document status
                      </Typography>
                    ) : (
                      <IconButton
                        size="sm"
                        variant="outlined"
                        color="danger"
                        onClick={() => uploadFile(file)}
                        title={file.uploadError}
                        disabled={disabled}
                      >
                        <RefreshIcon />
                      </IconButton>
                    ))}
                  <IconButton
                    size="sm"
                    variant="plain"
                    color="neutral"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeFile(file);
                    }}
                    disabled={
                      file.uploadStatus === "registering" ||
                      file.uploadStatus === "uploading" ||
                      file.uploadStatus === "processing" ||
                      file.uploadStatus === "indexing"
                    }
                    aria-label="Remove file"
                  >
                    <CloseIcon />
                  </IconButton>
                </Stack>
              </Box>
            ))}
          </Stack>

          {/* Error message */}
          {error && (
            <Typography level="body-sm" color="danger" sx={{ mt: 1 }}>
              {error}
            </Typography>
          )}
        </Box>

        <Modal
          open={showConfirmation}
          onClose={() => handleConfirmation(false)}
        >
          <ModalDialog>
            <DialogTitle>{confirmationTitle}</DialogTitle>
            <Stack spacing={2}>
              <Typography>{confirmationMessage}</Typography>
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button
                  variant="outlined"
                  color="neutral"
                  onClick={() => handleConfirmation(false)}
                >
                  {confirmationButtons[0].label}
                </Button>
                <Button
                  variant="solid"
                  color="primary"
                  onClick={() => handleConfirmation(true)}
                >
                  {confirmationButtons[1].label}
                </Button>
              </Stack>
            </Stack>
          </ModalDialog>
        </Modal>
      </>
    );
  },
);

export type { FileUploaderRef };
export default FileUploader;
